
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAllOrders } from '../context/AllOrdersContext';
import { useGeolocation } from '../hooks/useGeolocation';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/client';
import { AppContext } from '../context/AppContext';
import { DeliveryIcon, ChartBarIcon, OrderIcon } from '../components/icons';
import { parseDate, formatDateReadable } from '../utils/dateUtils';


const LivreurDashboardScreen: React.FC = () => {
    const { currentUser, updateUserProfile } = useAuth();
    const { placedOrders, loading: ordersLoading } = useAllOrders();
    const { startTracking, stopTracking, requestPermissions, getCurrentPosition } = useGeolocation(currentUser?.uid);
    const { setView } = React.useContext(AppContext);

    const [updatingStatus, setUpdatingStatus] = useState(false);

    const isOnline = currentUser?.isOnline || false;

    const handleToggleOnline = async () => {
        if (!currentUser) return;
        setUpdatingStatus(true);
        try {
            const newStatus = !isOnline;

            if (newStatus) {
                const hasPermission = await requestPermissions();
                if (!hasPermission) {
                    alert("La localisation est indispensable pour vous mettre en ligne.");
                    setUpdatingStatus(false);
                    return;
                }
                await getCurrentPosition();
            }

            if (newStatus) {
                await startTracking();
            } else {
                await stopTracking();
            }

            await updateUserProfile({ isOnline: newStatus });
        } catch (error) {
            console.error('Error toggling online status:', error);
            alert('Erreur lors du changement de statut');
        } finally {
            setUpdatingStatus(false);
        }
    };

    // Calculate Today's Stats
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const todayOrders = placedOrders.filter(o => {
        const orderDate = parseDate(o.timestamp);
        return orderDate >= today && o.assignedDriver?.id === currentUser?.uid;
    });

    const todayEarnings = todayOrders
        .filter(o => o.status === 'Livrée' || o.status === 'Terminée')
        .reduce((sum, o) => sum + (o.deliveryFee || 0), 0);

    const activeOrder = placedOrders.find(
        o => o.assignedDriver?.id === currentUser?.uid &&
            o.livreurStatus === 'accepted' &&
            ['En attente', 'En préparation', 'Prête', 'Envoyée'].includes(o.status)
    );

    const pendingOrdersCount = placedOrders.filter(o => {
        // 1. Explicitly assigned to ME
        if (o.livreurId === currentUser?.uid && o.livreurStatus === 'pending') {
            return true;
        }
        // 2. Available in my Wilaya
        if ((!o.livreurId || o.livreurId === '') && o.restaurantWilaya === currentUser?.wilaya) {
            return ['En attente', 'En préparation', 'Prête'].includes(o.status);
        }
        return false;
    }).length;



    return (
        <div className="min-h-screen bg-gray-50 pb-24">
            {/* Header */}
            <div className="bg-white shadow-sm p-5 sticky top-0 z-10 flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-black text-gray-900 tracking-tighter uppercase italic leading-none">
                        Dashboard
                    </h1>
                    <p className="text-[10px] font-black text-gray-900 uppercase tracking-[0.2em] italic mt-1">Bonjour, {currentUser?.name} ⚡</p>
                </div>
                <div className="flex bg-gray-100 p-1 rounded-full items-center gap-2">
                    <span className={`text-[10px] uppercase font-bold px-2 ${isOnline ? 'text-green-600' : 'text-gray-400'}`}>
                        {isOnline ? 'En ligne' : 'Hors ligne'}
                    </span>
                    <button
                        onClick={handleToggleOnline}
                        disabled={updatingStatus}
                        className={`w-12 h-6 rounded-full transition-colors relative ${isOnline ? 'bg-green-500' : 'bg-gray-300'} ${updatingStatus ? 'opacity-50' : ''}`}
                    >
                        <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${isOnline ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                </div>
            </div>

            <div className="p-4 space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-3">
                            <ChartBarIcon className="w-6 h-6" />
                        </div>
                        <p className="text-xs text-gray-500 font-medium">Gains aujourd'hui</p>
                        <p className="text-2xl font-bold text-gray-800">{todayEarnings} DA</p>
                    </div>
                    <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mb-3">
                            <OrderIcon className="w-6 h-6" />
                        </div>
                        <p className="text-xs text-gray-500 font-medium">Commandes</p>
                        <p className="text-2xl font-bold text-gray-800">{todayOrders.length}</p>
                    </div>
                </div>

                {/* Status Notice */}
                {!isOnline && (
                    <div className="bg-amber-50 border border-amber-100 p-4 rounded-xl flex items-start gap-3">
                        <span className="text-xl">⚠️</span>
                        <div>
                            <p className="text-sm font-bold text-amber-800">Vous êtes hors ligne</p>
                            <p className="text-xs text-amber-700">Mettez-vous en ligne pour commencer à recevoir des missions de livraison.</p>
                        </div>
                    </div>
                )}

                {/* Notifications Area */}
                {pendingOrdersCount > 0 && (
                    <button
                        onClick={() => setView({ screen: 'livreurHome' })}
                        className="w-full bg-red-600 text-white p-4 rounded-2xl shadow-lg shadow-red-200 flex items-center justify-between animate-pulse"
                    >
                        <div className="flex items-center gap-3">
                            <span className="text-2xl">📦</span>
                            <div className="text-left">
                                <p className="font-bold text-lg">{pendingOrdersCount} Nouvelle(s) mission(s)</p>
                                <p className="text-xs text-red-100 opacity-80">Cliquez pour voir les détails</p>
                            </div>
                        </div>
                        <span className="text-xl">→</span>
                    </button>
                )}

                {/* Active Mission Card */}
                <section>
                    <h2 className="text-xl font-black text-gray-900 tracking-tighter uppercase italic leading-none mb-4 px-1">Mission en cours</h2>
                    {activeOrder ? (
                        <div
                            onClick={() => setView({ screen: 'livreurActiveOrder', params: { orderId: activeOrder.id } })}
                            className="bg-white border-2 border-green-500 p-4 rounded-2xl shadow-sm relative overflow-hidden animate-fade-in"
                        >
                            <div className="absolute top-0 right-0 bg-green-500 text-white px-3 py-1 text-[10px] font-bold rounded-bl-xl uppercase tracking-widest italic">Actif</div>
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="font-extrabold text-lg text-gray-800 tracking-tight">{activeOrder.items[0]?.restaurantName}</h3>
                                    <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Vers: {activeOrder.customerAddress?.slice(0, 30)}...</p>
                                </div>
                                <span className="text-lg font-black text-green-600">{activeOrder.deliveryFee} DA</span>
                            </div>
                            <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Statut: <span className="text-blue-600 italic">{activeOrder.status}</span></span>
                                <span className="text-xs font-black text-green-600 underline uppercase tracking-tighter italic">Voir la carte →</span>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white py-12 flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 animate-fade-in">
                            <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                                <DeliveryIcon className="w-6 h-6 text-gray-200" />
                            </div>
                            <p className="text-gray-400 font-bold text-[10px] uppercase tracking-[0.2em] italic">Aucune mission active</p>
                        </div>
                    )}
                </section>

                {/* Quick History List */}
                <section>
                    <div className="flex justify-between items-end mb-3 px-1">
                        <h2 className="text-sm font-black text-gray-900 tracking-tighter uppercase italic underline decoration-red-600 decoration-2 underline-offset-4">Dernières missions</h2>
                        <button onClick={() => setView({ screen: 'livreurOrders' })} className="text-xs font-bold text-red-600">Voir tout</button>
                    </div>
                    <div className="space-y-3">
                        {todayOrders.slice(0, 3).map(order => (
                            <div key={order.id} className="bg-white p-3 rounded-xl shadow-sm opacity-70">
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                            <DeliveryIcon className="w-4 h-4 text-gray-400" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-gray-800">{order.items[0]?.restaurantName}</p>
                                            <p className="text-[10px] text-gray-400">{formatDateReadable(order.timestamp)}</p>
                                        </div>
                                    </div>
                                    <p className="text-sm font-bold text-gray-600">+{order.deliveryFee} DA</p>
                                </div>
                            </div>
                        ))}
                        {todayOrders.length === 0 && (
                            <p className="text-center text-xs text-gray-400 py-4 italic">Pas de missions terminées aujourd'hui</p>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default LivreurDashboardScreen;
